import { Grid, ListItemText, Menu, MenuItem } from '@mui/material';
import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ReactCrop from 'react-image-crop';

const calculateSize = (props) => {
    const { aspect, height } = props;
    const cropWidth = height * aspect;
    const cropHeight = height;
    return { cropWidth, cropHeight };
};

const ImageCrop = forwardRef((props, ref) => {
    const { src, srcName, defaultAR, minWidth, minHeight, savePhoto } = props;
    const { t } = useTranslation();
    const [aspectRatioList] = useState([
        { id: 1, label: 'Landscape (16/9)', value: 16 / 9 },
        // { id: 2, label: 'Portrait (9/16)', value: 9 / 16 },
        { id: 2, label: 'Portrait (4/5)', value: 4 / 5 },
        { id: 3, label: 'Square (1/1)', value: 1 },
        { id: 4, label: 'A4 (21/29.7)', value: 21 / 29.7 },
        { id: 5, label: 'A3 (29.7/42)', value: 29.7 / 42 },
        { id: 6, label: 'A2 (42/59.4)', value: 42 / 59.4 },
        { id: 7, label: 'A1 (59.4/84.1)', value: 59.4 / 84.1 },
        { id: 8, label: 'A0 (84.1/118.9)', value: 84.1 / 118.9 },
        { id: 9, label: 'Free', value: undefined }
    ]);
    const [aspect, setAspect] = useState(defaultAR);
    const [selectedId, setSelectedId] = useState(9);
    const [imgSize, setImgSize] = useState(calculateSize(aspect ?? 1, 200));
    const [crop, setCrop] = useState({ aspect, unit: 'px', x: 25, y: 25, width: imgSize?.cropWidth, height: imgSize?.cropHeight });
    const [completedCrop, setCompletedCrop] = useState(null);
    const [rotation, setRotation] = useState(0);
    const [flip, setFlip] = useState(false);
    const imgRef = useRef(null);
    const [anchorEl, setAnchorEl] = useState(null);
    const openArMenu = Boolean(anchorEl);

    const handleRotation = () => {
        const image = new Image();
        image.src = src;

        image.onload = () => {
            const canvas = document.createElement('canvas');
            const ctx = canvas.getContext('2d');

            if (rotation === 0 || rotation === 180) {
                canvas.width = image.height;
                canvas.height = image.width;
            } else {
                canvas.width = image.width;
                canvas.height = image.height;
            }

            ctx.clearRect(0, 0, canvas.width, canvas.height);
            ctx.translate(canvas.width / 2, canvas.height / 2);
            ctx.rotate(((rotation + 90) * Math.PI) / 180);
            setRotation((rotation + 90) % 360);
            ctx.drawImage(image, -image.width / 2, -image.height / 2);

            const rotatedImageSrc = canvas.toDataURL();
            imgRef.current.src = rotatedImageSrc;
        };
    };

    useImperativeHandle(ref, () => ({
        handleSavePhoto() {
            if (!completedCrop || !imgRef.current) {
                window.alert(t('NoCropSelected'));
                return;
            }
            const crop = completedCrop;

            // Create canvas and get 2D context
            const image = imgRef.current;
            const canvas = document.createElement('canvas');
            const ctx = canvas.getContext('2d');

            // Get the image scales
            const scaleX = image.naturalWidth / image.width;
            const scaleY = image.naturalHeight / image.height;

            // Get the device pixel ratio
            const pixelRatio = window.devicePixelRatio;

            // Set the canvas width and height

            // canvas.width = image.width * scaleX;
            // canvas.height = image.height * scaleY;

            canvas.width = Math.floor(crop.width * scaleX * pixelRatio);
            canvas.height = Math.floor(crop.height * scaleY * pixelRatio);

            // Set the canvas style width and height
            ctx.scale(pixelRatio, pixelRatio);

            // Set the image smoothing quality
            ctx.imageSmoothingQuality = 'high';

            // Set the image scales
            const cropX = crop.x * scaleX;
            const cropY = crop.y * scaleY;

            // Rotate the image
            const TO_RADIANS = Math.PI / 180;
            const rotateRads = rotation * TO_RADIANS;

            // Get the center of the image
            const centerX = image.naturalWidth / 2;
            const centerY = image.naturalHeight / 2;

            // Save the context
            ctx.save();

            // Draw the image
            ctx.translate(-cropX, -cropY);
            ctx.translate(centerX, centerY);
            ctx.scale(flip ? -1 : 1, 1);
            ctx.rotate(rotateRads);
            ctx.translate(-centerX, -centerY);
            ctx.drawImage(image, 0, 0, image.naturalWidth, image.naturalHeight, 0, 0, image.naturalWidth, image.naturalHeight);

            // Restore the context
            ctx.restore();

            // Convert the canvas to blob
            canvas.toBlob((blob) => {
                const file = new File([blob], `${srcName ?? 'cropped_image.jpg'}`, { type: 'image/jpeg' });
                savePhoto(file);
            }, 'image/jpeg');
        },
        handleSetRotation() {
            setCrop(null);
            handleRotation();
            setCompletedCrop(false);
        },
        handleSetFlip() {
            setCrop(null);
            setFlip((prev) => !prev);
            setCompletedCrop(false);
        },
        handleOpenMenu(event) {
            setAnchorEl(event.currentTarget);
        }
    }));

    const handleCloseArMenu = () => {
        setAnchorEl(null);
    };

    const onLoad = useCallback((img) => {
        imgRef.current = img.target;
    }, []);

    const handleSelectAspect = (aspect, id) => {
        setCompletedCrop(null);
        setSelectedId(id);
        setAspect(aspect);
        setAnchorEl(null);
    };

    const handleComletedCrop = (crop) => {
        setCompletedCrop(crop);
    };

    useEffect(() => {
        setAspect(defaultAR);
    }, [defaultAR]);

    useEffect(() => {
        setImgSize(calculateSize(aspect ?? 1, 200));
        setCrop({ ...crop, aspect, width: imgSize?.cropWidth, height: imgSize?.cropHeight });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [aspect]);

    return (
        <Grid container spacing={2}>
            <Grid item container justifyContent="center" alignItems="center" xs={12}>
                <ReactCrop
                    src={src}
                    aspect={aspect}
                    onImageLoaded={onLoad}
                    onComplete={handleComletedCrop}
                    minWidth={minWidth}
                    minHeight={minHeight}
                    crop={crop}
                    onChange={(c) => setCrop(c)}
                >
                    <img src={src} onLoad={onLoad} alt={srcName} style={{ transform: `scaleX(${flip ? -1 : 1})`, height: '55vh' }} />
                </ReactCrop>
            </Grid>
            <Menu id="aspect-menu" anchorEl={anchorEl} onClose={handleCloseArMenu} open={openArMenu}>
                {aspectRatioList.map((item) => (
                    <MenuItem
                        key={item.id}
                        onClick={() => handleSelectAspect(item?.value, item?.id)}
                        sx={{ background: selectedId === item.id ? '#4d79bc' : '' }}
                    >
                        <ListItemText>{item.label}</ListItemText>
                    </MenuItem>
                ))}
            </Menu>
        </Grid>
    );
});

export default ImageCrop;
